/* eslint-disable no-multi-assign */
/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import gsap from 'gsap';

import {
    Scene,
    WebGLRenderer,
    PerspectiveCamera,
    Mesh,
    MeshLambertMaterial,
    HemisphereLight,
    FogExp2,
    Object3D,
    DoubleSide,
    LoadingManager,
    Color,
} from 'three';

import { TGALoader } from 'three/examples/jsm/loaders/TGALoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';

import { useUrlGenerator } from '../../contexts/RoutesContext';
import { useTreesReady, useSetTreesReady } from '../../contexts/PagesContext';

import useAnimationFrame from '../../hooks/useAnimationFrame';
import useWindowSize from '../../hooks/useWindowSize';

import Link from './Link';

import logo from '../../../img/logo.svg';
import forestData from '../../data/forest';

import styles from '../../../styles/partials/trees.module.scss';

const propTypes = {
    maxAlpha: PropTypes.number,
    alphaTestValue: PropTypes.number,
    logoResizeDelay: PropTypes.number,
    debug: PropTypes.bool,
    rotateScene: PropTypes.bool,
    initialSceneRotation: PropTypes.number,
};

const defaultProps = {
    maxAlpha: 0.7,
    alphaTestValue: 0.1,
    logoResizeDelay: 3000,
    debug: false,
    rotateScene: false,
    initialSceneRotation: 0,
};

const TreesPartial = ({
    maxAlpha,
    alphaTestValue,
    logoResizeDelay,
    debug,
    rotateScene,
    initialSceneRotation,
}) => {
    const url = useUrlGenerator();
    const { width, height } = useWindowSize();
    const treesReady = useTreesReady();
    const setTreesReady = useSetTreesReady();
    const mouseRef = useRef(null);

    const [ready, setReady] = useState(debug);
    useEffect(() => {
        let timeout = null;
        if (ready) {
            timeout = setTimeout(() => {
                setTreesReady(true);
            }, logoResizeDelay);
        }
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
        };
    }, [ready, setTreesReady, logoResizeDelay]);

    // Max loading time
    useEffect(() => {
        const timeout = setTimeout(() => {
            setTreesReady(true);
        }, 5000);
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
        };
    }, [setTreesReady]);

    // 3D layer  --------------------------

    const canvasRef = useRef(null);
    const camera = useRef(null);
    const scene = useRef(null);
    const renderer = useRef(null);

    const quebecMaterials = useRef([]);
    const notQuebecMaterials = useRef([]);
    const forestQuebec = useRef(null);
    const forestNotQuebec = useRef(null);

    useEffect(() => {
        const { offsetWidth: canvasWidth, offsetHeight: canvasHeight } = canvasRef.current;
        camera.current = new PerspectiveCamera(75, canvasWidth / canvasHeight, 1, 1100);
        const cameraContainer = new Object3D();
        cameraContainer.add(camera.current);
        cameraContainer.position.z = 30;
        cameraContainer.position.y = -10;
        camera.current.lookAt(0, 0, 0);
        scene.current = new Scene();
        scene.current.add(cameraContainer);
        scene.current.add(new HemisphereLight(0xffffff, 0xffffff, 1.0));
        scene.current.fog = new FogExp2(0x00005c, 0.03);
        scene.current.rotation.y = initialSceneRotation;

        const forest = new Object3D();
        forest.position.y = -15;

        forestQuebec.current = new Object3D();
        forestNotQuebec.current = new Object3D();

        forest.add(forestQuebec.current);
        forest.add(forestNotQuebec.current);
        scene.current.add(forest);

        const customMaterial = new MeshLambertMaterial({
            emissive: 0x080808,
            fog: true,
            transparent: true,
            side: DoubleSide,
            alphaTest: alphaTestValue,
            opacity: maxAlpha,
        });

        const loadingManager = new LoadingManager(() => {
            setTimeout(setReady, 1000, true);
        });
        const tgaLoader = new TGALoader();
        const mtlLoader = new MTLLoader();
        mtlLoader.setPath('media/textures3d/');
        loadingManager.addHandler(/\.tga$/i, tgaLoader);

        forestData.forEach((forestElement) => {
            const {
                url: elUrl = null,
                Loader = FBXLoader,
                copies = [],
                quebec = false,
                mtl = null,
                customMtl = false,
            } = forestElement || {};

            const objLoader = new Loader(loadingManager);

            const loadModel = () => {
                objLoader.load(elUrl, (obj) => {
                    copies.forEach((copy) => {
                        const clonedObj = obj.clone();

                        const {
                            color = quebec ? 0x5ad588 : 0xff66cc,
                            position = null,
                            rotation = null,
                            scale = 1,
                        } = copy || {};

                        if (quebec) {
                            forestQuebec.current.add(clonedObj);
                        } else {
                            forestNotQuebec.current.add(clonedObj);
                        }

                        const { x: posX = 0, y: posY = 0, z: posZ = 0 } = position || {};
                        const { x: rotX = 0, y: rotY = 0, z: rotZ = 0 } = rotation || {};
                        clonedObj.position.x = posX;
                        clonedObj.position.y = posY;
                        clonedObj.position.z = posZ;
                        clonedObj.rotateX(rotX);
                        clonedObj.rotateY(rotY);
                        clonedObj.rotateZ(rotZ);
                        clonedObj.scale.x = clonedObj.scale.y = clonedObj.scale.z = scale;

                        clonedObj.traverse((objChild) => {
                            if (objChild instanceof Mesh) {
                                if (customMtl) {
                                    objChild.material = customMaterial.clone();
                                    objChild.material.color = new Color(color);
                                } else {
                                    objChild.material.color = new Color(color);
                                    objChild.material.emissive = new Color(color);
                                    objChild.material.transparent = true;
                                    objChild.material.fog = true;
                                    objChild.material.alphaTest = alphaTestValue;
                                    objChild.material.opacity = maxAlpha;
                                    objChild.material.side = DoubleSide;
                                }

                                if (quebec) {
                                    quebecMaterials.current.push(objChild.material);
                                } else {
                                    notQuebecMaterials.current.push(objChild.material);
                                }
                            }
                        });
                    });
                });
            };

            if (mtl !== null) {
                mtlLoader.load(mtl, (materials) => {
                    materials.preload();
                    objLoader.setMaterials(materials);
                    loadModel();
                });
            } else {
                loadModel();
            }
        });

        renderer.current = new WebGLRenderer({
            canvas: canvasRef.current,
            antialias: true,
            alpha: true,
        });
        renderer.current.setPixelRatio(window.devicePixelRatio);
        renderer.current.setSize(canvasWidth, canvasHeight);

        return () => {
            camera.current = null;
            scene.current = null;
            renderer.current = null;
        };
    }, [setReady, maxAlpha, alphaTestValue, initialSceneRotation]);

    const render3D = useCallback(() => {
        if (rotateScene) {
            forestQuebec.current.rotation.y -= 0.0005;
            forestNotQuebec.current.rotation.y += 0.0005;
        }

        renderer.current.render(scene.current, camera.current);
    }, [rotateScene]);

    const lastMouseRef = useRef(null);
    const animateMaterialsFromMouse = useCallback(() => {
        if (quebecMaterials.current.length === 0) {
            return;
        }

        const { x: mouseX = width / 2 } = mouseRef.current || {};

        const percent = mouseX / width;

        const qcPercent = 2 - Math.max(0.5, percent) * 2;
        const notQcPercent = Math.min(0.5, percent) * 2;

        const finalQcPercent = qcPercent * (maxAlpha - alphaTestValue) + alphaTestValue;
        const finalNotQcPercent = notQcPercent * (maxAlpha - alphaTestValue) + alphaTestValue;

        gsap.to(quebecMaterials.current, {
            opacity: finalQcPercent,
            duration: 1.5,
        });
        gsap.to(notQuebecMaterials.current, {
            opacity: finalNotQcPercent,
            duration: 1.5,
        });
    }, [width, maxAlpha, alphaTestValue]);

    const animateCameraFromMouse = useCallback(() => {
        const { x: mouseX = width / 2, y: mouseY = height / 2 } = mouseRef.current || {};
        const xPercent = (mouseX / width) * 2 - 1;
        const yPercent = (mouseY / height) * 2 - 1;
        const movementAmount = 1;

        gsap.to(camera.current.position, {
            x: xPercent * movementAmount,
            y: -yPercent * movementAmount,
            duration: 1.5,
        });
    }, [width, height]);

    const animate = useCallback(() => {
        render3D();

        if (lastMouseRef.current === mouseRef.current) {
            return;
        }
        lastMouseRef.current = mouseRef.current;

        animateMaterialsFromMouse();
        animateCameraFromMouse();
    }, [render3D, animateMaterialsFromMouse, animateCameraFromMouse]);

    useAnimationFrame(animate, { disabled: false });

    useEffect(() => {
        if (camera.current !== null && renderer.current !== null) {
            camera.current.aspect = width / height;
            camera.current.updateProjectionMatrix();
            renderer.current.setSize(width, height);
        }
    }, [width, height]);

    const onMouseMove = useCallback((e) => {
        const { clientX, clientY } = e;
        mouseRef.current = { x: clientX, y: clientY };
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', onMouseMove);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, [onMouseMove]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.ready]: ready,
                    [styles.logoResized]: treesReady,
                },
            ])}
        >
            <div className={styles.canvasContainer}>
                <canvas className={styles.canvas} ref={canvasRef} />
            </div>
            <Link to={url('home')}>
                <img className={styles.logo} src={logo} alt="Logo" />
            </Link>
            <div className={styles.texture} />
        </div>
    );
};

TreesPartial.propTypes = propTypes;
TreesPartial.defaultProps = defaultProps;

export default TreesPartial;
